// Reading the routing data: one answer per page (`/api/routing/worklist`, `/shipping` or `/history`), read
// again when A2000 or Deposco loaded new rows (`useSourceVersion`, asked every 15 s), on focus, and every
// 5 minutes as a safety net, so new source rows and everyone's saves show up without a refresh.

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Query } from "@tanstack/react-query";
import { ROUTING_API, carrierOptions, holidayCalendar, lineCarrierIndex, versionStep } from "@app/shared";
import type {
  CarrierRow,
  Holiday,
  LineCarrierRow,
  LineView,
  Mode,
  MyViewPayload,
  PreRouteData,
  PreRouteOrderMatches,
  PreRouteSource,
  RoutingListsConfig,
  RoutingPayload,
  SourceVersion,
} from "@app/shared";
import { apiGet, apiPost, buildQuery } from "@axny/platform-ui";
import { afterSaves } from "./writer";

/** The pages' own refetch: only a safety net, as a new source load is told by `useSourceVersion`. */
export const SAFETY_REFETCH_MS = 5 * 60_000;

/** The pages that read the routing data: the Worklist, Shipping and History. */
export type DataPage = Mode | "shipping";

/** Shipping's Ship log days, or History's finish days (`from`–`to`): the answer holds only theirs. */
export interface ShipDays {
  from?: string | null;
  to?: string | null;
}

export const routingKey = (mode: DataPage, days: ShipDays = {}) =>
  mode === "worklist" ? (["routing", mode] as const) : (["routing", mode, days.from ?? "", days.to ?? ""] as const);

/**
 * The Worklist reads every line; Shipping only its pickups' lines and the Ship log's days, History
 * only the lines that finished on its days (`subset.ts` on the server; every one with no days).
 */
export function useRoutingData(mode: DataPage, days: ShipDays = {}) {
  return useQuery({
    queryKey: routingKey(mode, days),
    queryFn: ({ signal }) =>
      apiGet<RoutingPayload>(
        `${ROUTING_API}/${mode}${buildQuery({
          from: mode === "worklist" ? undefined : (days.from ?? undefined),
          to: mode === "worklist" ? undefined : (days.to ?? undefined),
        })}`,
        signal,
      ),
    refetchInterval: SAFETY_REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
    // A new range keeps the page on screen while its days load.
    placeholderData: (previous) => previous,
  });
}

/** The lines and pickups the System log's changes name (`POST /api/routing/lines`), for Go to line and Restore. */
export const linesKey = (ids: { lineIds: readonly string[]; pickupIds: readonly string[] }) =>
  ["routing", "lines", [...ids.lineIds].sort().join(","), [...ids.pickupIds].sort().join(",")] as const;

export function useLinesData(ids: { lineIds: readonly string[]; pickupIds: readonly string[] }, enabled: boolean) {
  return useQuery({
    queryKey: linesKey(ids),
    queryFn: () =>
      apiPost<RoutingPayload>(`${ROUTING_API}/lines`, {
        lineIds: ids.lineIds,
        pickupIds: ids.pickupIds,
      }),
    enabled,
    refetchInterval: SAFETY_REFETCH_MS,
    staleTime: 10_000,
    placeholderData: (previous) => previous,
  });
}

/** Add a line's search: the open lines a typed PO, PT or SO # names (`GET /api/routing/lines/find`). */
export const findLines = (q: string) => apiGet<RoutingPayload>(`${ROUTING_API}/lines/find${buildQuery({ q })}`);

/** The pre-routes, with their orders and the lines a move writes (`GET /api/routing/pre-routes`). */
export type PreRoutePayload = RoutingPayload & PreRouteData;
export const PRE_ROUTES_KEY = ["routing", "pre-routes"] as const;

/**
 * Read on every routing page (the tab's badge, the move): read again on a new source load, on focus
 * and every 5 minutes, so a pre-route moves on the next refresh of whoever has a page open.
 */
export function usePreRoutes(enabled = true) {
  return useQuery({
    queryKey: PRE_ROUTES_KEY,
    queryFn: ({ signal }) => apiGet<PreRoutePayload>(`${ROUTING_API}/pre-routes`, signal),
    enabled,
    refetchInterval: SAFETY_REFETCH_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** Add a pre-route: what was typed, as the form checks it (`GET /api/routing/pre-routes/order`). */
/** The orders of an A2000 order # (CTRL_NO) or a Deposco SO #, or of a customer PO: one, or many. */
export const lookupOrder = (no: string, source: PreRouteSource = "A2000") =>
  apiGet<PreRouteOrderMatches>(`${ROUTING_API}/pre-routes/order${buildQuery({ no, source })}`);

/** `/api/routing/holidays`: the days business days skip, as the server counts them. */
export interface HolidaysPayload {
  /** Only those `ROUTING_HOLIDAYS` keeps: the server already left the others out. */
  days: Holiday[];
  degraded: boolean;
}

/**
 * The holidays, loaded once a session: the same calendar the server's reports count with, so the
 * grid's window labels match them. The server refreshes its own copy daily.
 */
export function useHolidays(enabled = true) {
  return useQuery({
    queryKey: ["routing", "holidays"],
    queryFn: ({ signal }) => apiGet<HolidaysPayload>(`${ROUTING_API}/holidays`, signal),
    enabled,
    staleTime: Infinity,
    select: (p) => ({ calendar: holidayCalendar(p.days, { us: true, hebcal: true }), degraded: p.degraded }),
  });
}

export const MY_VIEW_KEY = ["routing", "my-view"] as const;

/**
 * The person's saved default of each page and their customers (`assignments.ts`): read before the
 * Worklist or History opens, so its first view is already theirs; again after a save.
 */
export function useMyView() {
  return useQuery({
    queryKey: MY_VIEW_KEY,
    queryFn: ({ signal }) => apiGet<MyViewPayload>(`${ROUTING_API}/my-view`, signal),
    staleTime: 60_000,
    retry: 1,
  });
}

export function useListsConfig(enabled: boolean) {
  return useQuery({
    queryKey: ["routing", "lists-config"],
    queryFn: ({ signal }) => apiGet<RoutingListsConfig>(`${ROUTING_API}/lists-config`, signal),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * The carriers a pickup or a shipment may name (item 8, `carriers.ts`): every row of A2000's and
 * Deposco's list, placeholders too (their names are Schedule pickup's hint). Kept 10 minutes.
 */
export function useCarriers() {
  return useQuery({
    queryKey: ["routing", "carriers"],
    queryFn: ({ signal }) => apiGet<{ carriers: CarrierRow[] }>(`${ROUTING_API}/carriers`, signal),
    staleTime: 10 * 60_000,
    retry: 1,
    select: (p) => ({ rows: p.carriers, options: carrierOptions(p.carriers) }),
  });
}

/** Refs per line-carriers request (the server takes as many). */
const LINE_CARRIER_REFS = 1000;

/** The lines' own carriers, BOL #s and appointment IDs: their pick tickets' (A2000) or SOs' (Deposco). */
export function useLineCarriers(lines: readonly Pick<LineView, "source" | "pts">[], enabled = true) {
  const refs = [...new Set(lines.flatMap((l) => l.pts.map((p) => `${l.source}|${p.pickTicket}`)))].sort();
  return useQuery({
    queryKey: ["routing", "line-carriers", refs],
    // A thousand refs a request: the platform parses a JSON body only up to 64 KB.
    queryFn: async () => {
      const parts = await Promise.all(
        Array.from({ length: Math.ceil(refs.length / LINE_CARRIER_REFS) }, (_, i) =>
          apiPost<{ lineCarriers: LineCarrierRow[] }>(`${ROUTING_API}/line-carriers`, {
            refs: refs.slice(i * LINE_CARRIER_REFS, (i + 1) * LINE_CARRIER_REFS),
          }),
        ),
      );
      return { lineCarriers: parts.flatMap((p) => p.lineCarriers) };
    },
    enabled: enabled && refs.length > 0,
    staleTime: 60_000,
    retry: 1,
    select: (p) => lineCarrierIndex(p.lineCarriers),
  });
}

export const VERSION_KEY = ["routing", "version"] as const;

/**
 * When A2000 and Deposco last loaded new rows (`GET /api/routing/version`, no Fabric query): asked every
 * 15 s, in a background tab too. Failing, the pages rely on their 5-minute refetch and say nothing.
 */
export function useSourceVersion() {
  return useQuery({
    queryKey: VERSION_KEY,
    queryFn: ({ signal }) => apiGet<SourceVersion>(`${ROUTING_API}/version`, signal),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });
}

/** Routing keys whose answers do not come from the A2000 or Deposco rows: never read again for a new load. */
const NOT_SOURCE = new Set(["version", "holidays", "lists-config", "carriers", "my-view", "admin"]);
const fromSource = (q: Query) => q.queryKey[0] === "routing" && !NOT_SOURCE.has(String(q.queryKey[1]));

/**
 * Mounted once, by the shell, on every routing page: when a served marker is newer (`versionStep`) than
 * the last seen, every page's data is read again — once no save runs. The first answer is only recorded, and
 * a marker that goes back (another instance, a restart) never counts. An open cell editor, the
 * selection, a dialog and the scroll stay as they are: the answer replaces the data as a refetch does.
 */
export function SourceVersionWatch() {
  const version = useSourceVersion();
  const queryClient = useQueryClient();
  const seen = useRef<SourceVersion | null>(null);
  useEffect(() => {
    const now = version.data;
    if (!now) return;
    // Only the markers of rows the server serves: a newer one means the new rows are there to read.
    const { changed, seen: next } = versionStep(seen.current, now);
    seen.current = next;
    if (changed) afterSaves(() => void queryClient.invalidateQueries({ predicate: fromSource }));
  }, [version.data, queryClient]);
  return null;
}
